import UserAvatar from "@/src/components/ui-kit/user-avatar";
import { useAuth } from "@/src/contexts/auth.context";
import { sendNotification } from "@/src/services/api/notification/notification.service";
import { useGetAllUserProfilesQuery } from "@/src/services/profile/profile.hooks";
import { useUsersByIdsQuery } from "@/src/services/users.blocks.api";
import { useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Pressable, Text, View } from "react-native";
import Toast from "react-native-toast-message";
import ScButton from "../../ui-kit/button";

type BirthdayEntry = Readonly<{
  id: string;
  userId: string;
  name: string;
  avatar: string | null;
  dateLabel: string;
  isToday: boolean;
  dob: Date;
}>;

type BirthdayItemProps = Readonly<{
  entry: BirthdayEntry;
  onPress: (userId: string) => void;
  onWish: (userId: string, name: string) => void;
  isWishing: boolean;
  hasWished: boolean;
}>;

const BirthdayItem = ({
  entry,
  onPress,
  onWish,
  isWishing,
  hasWished,
}: BirthdayItemProps) => {
  const { t } = useTranslation();
  const isToday = entry?.isToday;

  const containerClass = isToday
    ? "bg-wheat border border-wheat"
    : "bg-ash border border-ash-3";

  const dateLabelClass = isToday ? "text-primary font-medium" : "text-slate";

  return (
    <Pressable
      onPress={() => onPress(entry.userId)}
      className={`relative flex-row items-center gap-3 p-2.5 rounded-xl overflow-hidden hover:bg-ash-3 ${containerClass}`}
      style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
    >
      {isToday && (
        <Text className="absolute -right-2 -top-1 text-6xl opacity-10">🎉</Text>
      )}

      {entry?.avatar ? (
        <Image
          source={{ uri: entry.avatar }}
          style={{ width: 38, height: 38, borderRadius: 19 }}
        />
      ) : (
        <UserAvatar
          firstName={entry?.name?.split(" ")[0]}
          lastName={entry?.name?.split(" ")[1]}
          size={38}
        />
      )}

      <View className="flex-1 min-w-0">
        <Text className="text-sm font-semibold text-onyx" numberOfLines={1}>
          {entry?.name}
        </Text>
        <Text className={`text-xs mt-0.5 ${dateLabelClass}`} numberOfLines={1}>
          {entry?.dateLabel}
        </Text>
      </View>

      {isToday && (
        <View style={{ width: 72 }} className="shrink-0 items-end">
          {/* wish button — sends notification, no navigation */}
          <ScButton
            text={hasWished ? "🎉" : t("BTN_WISH")}
            mode="contained"
            className="rounded-full"
            loading={isWishing}
            disabled={hasWished || isWishing}
            onPress={() => {
              onWish(entry.userId, entry.name);
            }}
          />
        </View>
      )}
    </Pressable>
  );
};

const parseDob = (dob: string): { month: number; day: number } | null => {
  const sanitized = dob.replace(/[<>]/g, "");
  const match = sanitized.match(/^(\d{1,2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  return { day: Number(match[1]), month: Number(match[2]) };
};

const filterUpcomingBirthdays = (
  profiles: ReturnType<typeof useGetAllUserProfilesQuery>["data"],
) => {
  if (!profiles?.length) return [];

  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const next30Days = new Date(todayMidnight);
  next30Days.setDate(todayMidnight.getDate() + 30);

  return profiles
    .filter((profile) => !!profile?.dob)
    .map((profile) => {
      const parsed = parseDob(profile?.dob as string);
      if (!parsed) return null;

      const { day, month } = parsed;
      const isToday =
        month - 1 === todayMidnight.getMonth() &&
        day === todayMidnight.getDate();

      const bday = new Date(todayMidnight.getFullYear(), month - 1, day);
      if (bday < todayMidnight && !isToday)
        bday.setFullYear(todayMidnight.getFullYear() + 1);

      if (bday > next30Days) return null;

      return { profile, day, month, isToday, bday };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a!.isToday && !b!.isToday) return -1;
      if (!a!.isToday && b!.isToday) return 1;
      return a!.bday.getTime() - b!.bday.getTime();
    })
    .slice(0, 5);
};

export const UpcomingBirthdaysWidget = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();

  const [wishingUserId, setWishingUserId] = useState<string | null>(null);
  const [wishedUserIds, setWishedUserIds] = useState<Set<string>>(new Set());

  const { data: allProfiles, isLoading: isProfilesLoading } =
    useGetAllUserProfilesQuery();

  const upcomingProfiles = filterUpcomingBirthdays(allProfiles);

  const userIds = [
    ...new Set(
      upcomingProfiles
        .map((p) => p?.profile?.CreatedBy)
        .filter(Boolean) as string[],
    ),
  ];

  const { data: usersData, isLoading: isUsersLoading } =
    useUsersByIdsQuery(userIds);

  const isLoading = isProfilesLoading || isUsersLoading;

  const usersMap = (usersData?.users ?? []).reduce(
    (acc, blocksUser) => ({
      ...acc,
      [blocksUser?.itemId]: {
        firstName: blocksUser?.firstName,
        lastName: blocksUser?.lastName,
        profileImageUrl: blocksUser?.profileImageUrl,
      },
    }),
    {} as Record<
      string,
      { firstName: string; lastName: string; profileImageUrl: string | null }
    >,
  );

  const upcomingBirthdays: BirthdayEntry[] = upcomingProfiles
    .map((item) => {
      if (!item) return null;
      const { profile, isToday, bday } = item;
      const blocksUser = usersMap[profile?.CreatedBy ?? ""];
      if (!blocksUser) return null;

      const daysUntil = Math.ceil(
        (bday.getTime() - new Date().setHours(0, 0, 0, 0)) /
        (1000 * 60 * 60 * 24),
      );

      const dateLabel = isToday
        ? t("LABEL_TODAY")
        : `${bday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · ${daysUntil}d`;

      return {
        id: profile?.ItemId,
        userId: profile?.CreatedBy,
        name: `${blocksUser.firstName} ${blocksUser.lastName}`,
        avatar: blocksUser.profileImageUrl ?? null,
        dateLabel,
        isToday,
        dob: bday,
      };
    })
    .filter((entry): entry is BirthdayEntry => entry !== null);

  const handleNavigateToProfile = (userId: string) => {
    router.push({
      pathname: "/profile/[id]",
      params: { id: userId },
    });
  };

  // send birthday wish notification to specific user
  const handleWish = async (userId: string, name: string) => {
    if (!user || wishingUserId || wishedUserIds.has(userId)) return;
    setWishingUserId(userId);
    try {
      await sendNotification({
        userIds: [userId],
        roles: [],
        denormalizedPayload: JSON.stringify({
          type: "birthday_wish",
          message: t("NOTIFY_BIRTHDAY_WISH", {
            firstName: user.firstName,
            lastName: user.lastName,
          }),
          actorId: user.itemId,
          actorName: `${user.firstName} ${user.lastName}`,
          actorAvatar: user.profileImageUrl,
        }),
        configuratoinName: "birthday-wish",
        responseKey: "birthday-action",
        responseValue: "birthday_wish",
      });
      setWishedUserIds((prev) => new Set(prev).add(userId));
      Toast.show({
        type: "success",
        text1: t("LABEL_BIRTHDAY_WISH_SENT", { name }),
      });
    } catch {
      Toast.show({
        type: "error",
        text1: t("ERROR_WISH_FAILED"),
      });
    } finally {
      setWishingUserId(null);
    }
  };

  if (isLoading) {
    return (
      <View className="rounded-2xl border border-ash-3 bg-chalk p-4 gap-3">
        <Text className="text-sm font-bold text-onyx">
          {t("LABEL_UPCOMING_BIRTHDAYS")}
        </Text>
        {[1, 2, 3].map((skeletonKey) => (
          <View
            key={skeletonKey}
            className="h-14 bg-ash-3 rounded-xl animate-pulse"
          />
        ))}
      </View>
    );
  }

  if (!upcomingBirthdays?.length) {
    return (
      <View className="rounded-2xl border border-ash-3 bg-chalk p-4 gap-3">

        <View className="-mx-4 -mt-4 px-4 py-3 bg-primary-1 rounded-t-2xl">
          <Text className="text-sm font-bold text-primary">
            {t("LABEL_UPCOMING_BIRTHDAYS")}
          </Text>
        </View>
        <Text className="text-xs text-slate text-center py-4">
          {t("LABEL_NO_UPCOMING_BIRTHDAYS")}
        </Text>
      </View>
    );
  }

  return (
    <View className="rounded-2xl border border-ash-3 bg-chalk p-4 gap-3">
      <View className="-mx-4 -mt-4 px-4 py-3 bg-primary-1 rounded-t-2xl">
        <Text className="text-sm font-bold text-primary">
          {t("LABEL_UPCOMING_BIRTHDAYS")}
        </Text>
      </View>
      <View className="gap-2">
        {upcomingBirthdays.map((birthdayEntry) => (
          <BirthdayItem
            key={birthdayEntry?.id}
            entry={birthdayEntry}
            onPress={handleNavigateToProfile}
            onWish={handleWish}
            isWishing={wishingUserId === birthdayEntry.userId}
            hasWished={wishedUserIds.has(birthdayEntry.userId)}
          />
        ))}
      </View>
    </View>
  );
};
